import { Box, Stack, Typography, useTheme } from '@mui/material';
import IconButton from '@watershed/ui-core/components/IconButton';
import SidebarOpenIcon from '@watershed/icons/components/SidebarLeftOpen';
import SidebarCloseIcon from '@watershed/icons/components/SidebarRightOpen';

import { GQPermissionType } from '@watershed/shared-universal/generated/graphql';
import useGlobalLocation from '@watershed/ui-core/hooks/useGlobalLocation';
import { Location } from 'history';

import { useHoverIntent } from 'react-use-hoverintent';

import { SetStateAction, useContext, useMemo, useState } from 'react';
import useHasPermission from '../../utils/useHasPermission';
import { UserContext } from '../../utils/UserContext';
import LogoBar from './LogoBar';
import NavSection from './NavSection';
import { SkipNavLink } from './SkipNav';
import UserBar from './UserBar';
import { DEFAULT_DURATION_MS, NavItemData, NavSectionData } from './utils';
import useSidebarFinanceVariant from './variants/SideBarFinanceVariant';
import useSidebarStandardVariant from './variants/SideBarStandardVariant';
import {
  SIDEBAR_BREAKPOINT_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
  SKIP_NAV_LINK_CLASS,
  getSharedSidebarStyles,
} from './variants/sharedSidebarStyles';
import { mixinSx } from '@watershed/style/styleUtils';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import {
  useIsOnFinancePage,
  useIsOnUnknownPage,
} from '../../utils/CurrentProductContext';
import { hasPermission } from '@watershed/shared-universal/utils/permissionUtils';
import { DevelopmentChip } from '../AdminChipBar';
import { getCurrentDevEnv } from '@watershed/shared-frontend/utils/devEnv';
import { useLingui } from '@lingui/react';
import { Trans, msg } from '@lingui/macro';

/*
    This file is just the parent SideBar that knows how to render and work with
    any variants we come up with in the future. It is expected that any variants
    export classes (visual theme) and the navEntries themselves.
*/

function convertURLSpecialChars(pathPart: string) {
  // Can add necessary special char replacements
  return pathPart.replace(/%20/g, ' ');
}

function getMatchScore(
  entry: NavItemData | NavSectionData,
  location: Location<any>
): number {
  // The match score is the length of the prefix match of the entry's path and
  // the current location path.
  const entryPathParts = entry.location.pathname.split('/').slice(1);
  const locationPathParts = location.pathname.split('/').slice(1);
  const minLength = Math.min(entryPathParts.length, locationPathParts.length);
  let i = 0;
  while (
    i < minLength &&
    entryPathParts[i] === convertURLSpecialChars(locationPathParts[i])
  ) {
    i++;
  }
  // Bonus point for all parts matching.
  const maxLength = Math.max(entryPathParts.length, locationPathParts.length);
  if (i === maxLength) {
    i++;

    // And an extra bonus if the fragment (hash) matches as well.
    if (location.hash === `#${entry.location.hash}`) {
      i++;
    }
  }
  return i;
}

function findActiveEntry(
  navEntries: Array<NavItemData | NavSectionData>,
  location: Location<any>,
  activeEntry: NavItemData | null = null,
  activeEntryMatchScore = 0
) {
  for (const entry of navEntries) {
    const matchScore = getMatchScore(entry, location);
    if (
      matchScore > activeEntryMatchScore ||
      (matchScore === activeEntryMatchScore && activeEntry === null)
    ) {
      activeEntry = entry;
      activeEntryMatchScore = matchScore;
    }
    if (entry.subentries?.length) {
      const subEntryResult = findActiveEntry(
        entry.subentries,
        location,
        activeEntry,
        activeEntryMatchScore
      );
      if (
        subEntryResult.matchScore > activeEntryMatchScore ||
        (subEntryResult.matchScore === activeEntryMatchScore &&
          activeEntry === null)
      ) {
        activeEntry = subEntryResult.activeEntry;
        activeEntryMatchScore = subEntryResult.matchScore;
      }
    }
  }
  return { activeEntry, matchScore: activeEntryMatchScore };
}

export default function SideBar({
  isCollapsed,
  setIsCollapsed,
  toggleCreateFinanceSavedView,
}: {
  isCollapsed: boolean;
  setIsCollapsed: (value: SetStateAction<boolean>) => void;
  toggleCreateFinanceSavedView: () => void;
}) {
  const { _ } = useLingui();
  const isOnUnknownPage = useIsOnUnknownPage();
  const isOnFinancePage = useIsOnFinancePage();
  const userContext = useContext(UserContext);
  const isAdmin = useHasPermission([GQPermissionType.Admin]);

  const [isSideBarHovered, hoverIntentRef, setIsSideBarHovered] =
    useHoverIntent({
      interval: 100,
      timeout: 200,
    });

  const [isHoverDisabled, setIsHoverDisabled] = useState(false);
  const appearsCollapsed = isCollapsed && !isHoverDisabled && !isSideBarHovered;

  const sharedSidebarStyles = getSharedSidebarStyles(useTheme());

  // TODO: Look to db for these two
  const canAccessFundMode =
    userContext.canAccessFinance &&
    hasPermission(
      userContext.permissions,
      [GQPermissionType.FinanceReadOnly, GQPermissionType.ManageFund],
      {
        allowAnyObject: true,
      }
    );
  const canAccessCorporateMode = userContext.canAccessCorporate;

  // This seems regrettable. We should find a better way to do this that doesn't call both hooks.
  const financeVariant = useSidebarFinanceVariant({
    funds: userContext.funds,
    views: userContext.financeSavedViews,
    toggleCreateFinanceSavedView,
  });
  const standardVariant = useSidebarStandardVariant();
  const { navEntries, syntheticNavEntries, contentAfterNavEntries } =
    isOnFinancePage ? financeVariant : standardVariant;

  // Is false when an error is thrown and `<ErrorFallback>` is rendered.
  const { hasInitialized } = useContext(UserContext);

  const { location } = useGlobalLocation();
  const hasHome = useHasPermission([GQPermissionType.ViewFootprintDetail]);

  const activeEntry = useMemo(() => {
    const { activeEntry } = findActiveEntry(
      [...navEntries, ...(syntheticNavEntries ?? [])],
      location
    );
    return activeEntry;
  }, [navEntries, syntheticNavEntries, location]);

  const onClick = () => {
    setIsHoverDisabled(true);
    setIsCollapsed((value) => !value);
    setIsSideBarHovered(false);
    setTimeout(() => {
      setIsHoverDisabled(false);
    }, DEFAULT_DURATION_MS);
  };

  const canToggle =
    (isOnFinancePage && canAccessCorporateMode) ||
    (!isOnFinancePage && canAccessFundMode);

  if (isOnUnknownPage) {
    return null;
  }

  return (
    <Stack
      data-test={TestIds.Sidebar}
      ref={hoverIntentRef}
      component="nav"
      aria-label={_(msg`Sidebar`)}
      data-appears-collapsed={appearsCollapsed}
      data-is-collapsed={isCollapsed}
      sx={mixinSx(sharedSidebarStyles.rootSxProps, {
        color: (theme) => (isOnFinancePage ? theme.palette.white : undefined),
        width: SIDEBAR_EXPANDED_WIDTH,
        boxShadow: 'none',
        '&[data-is-collapsed="true"]': {
          boxShadow: '0 0 8px rgba(0,0,0,0.3)',
        },
        '&[data-appears-collapsed="true"]': {
          width: SIDEBAR_COLLAPSED_WIDTH,
          boxShadow: 'none',
          ':hover': {
            width: SIDEBAR_COLLAPSED_WIDTH + 4,
          },
        },
      })}
    >
      <Box sx={sharedSidebarStyles.logoBarSxProps}>
        <SkipNavLink className={SKIP_NAV_LINK_CLASS}>
          <Typography
            variant="h3"
            component="span"
            sx={sharedSidebarStyles.skipNavLinkTextSxProps}
          >
            <Trans>Skip to main content</Trans>
          </Typography>
        </SkipNavLink>
        <LogoBar
          isOnFinancePage={isOnFinancePage}
          appearsCollapsed={appearsCollapsed}
          canToggle={canToggle}
          hasHome={hasHome}
        />
        <CollapseButton
          appearsCollapsed={appearsCollapsed}
          isCollapsed={isCollapsed}
          isSideBarHovered={isSideBarHovered}
          onClick={onClick}
        />
      </Box>
      {getCurrentDevEnv() === 'dev-proxy' && (
        <Box marginTop={-1} marginBottom={2} paddingX={2}>
          <DevelopmentChip fullWidth />
        </Box>
      )}
      {navEntries.map((section) => (
        <NavSection
          key={section.name}
          section={section}
          appearsCollapsed={appearsCollapsed}
          activeEntry={activeEntry}
        />
      ))}
      {contentAfterNavEntries
        ? contentAfterNavEntries({ appearsCollapsed, activeEntry })
        : null}
      {hasInitialized && (
        <UserBar
          isOnFinancePage={isOnFinancePage}
          appearsCollapsed={appearsCollapsed}
          isAdmin={isAdmin}
          data-test={TestIds.SidebarUserBar}
        />
      )}
    </Stack>
  );
}

function CollapseButton({
  appearsCollapsed,
  isCollapsed,
  isSideBarHovered,
  onClick,
}: {
  appearsCollapsed: boolean;
  isCollapsed: boolean;
  isSideBarHovered: boolean;
  onClick: () => void;
}) {
  const { _ } = useLingui();
  const sharedSidebarStyles = getSharedSidebarStyles(useTheme());

  return (
    <IconButton
      data-appears-collapsed={appearsCollapsed}
      data-is-collapsed={isCollapsed}
      data-is-sidebar-hovered={isSideBarHovered}
      sx={mixinSx(sharedSidebarStyles.collapseButtonSxProps, {
        opacity: 1,
        '[data-appears-collapsed="true"]': {
          opacity: 0,
        },
        [`@media (max-width: ${SIDEBAR_BREAKPOINT_WIDTH}px)`]: {
          '&[data-is-collapsed="true"][data-is-sidebar-hovered="true"]': {
            visibility: 'hidden',
          },
        },
      })}
      title={isCollapsed ? _(msg`Expand`) : _(msg`Collapse`)}
      onClick={onClick}
      size="large"
    >
      {isCollapsed ? (
        <SidebarOpenIcon color={sharedSidebarStyles.COLORS.TEXT_PRIMARY} />
      ) : (
        <SidebarCloseIcon color={sharedSidebarStyles.COLORS.TEXT_PRIMARY} />
      )}
    </IconButton>
  );
}
